import React from 'react';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import styled from 'styled-components';
import {Btn} from '../../../../shared/elements/btn/Btn';
import {Icon} from '../../../../shared/elements/icon/Icon';
import {Timeline} from '../../../../shared/elements/timeline/Timeline';
import {TimelineEntryFieldUpdate, processFieldUpdates} from './FieldUpdate';

export const EntryDate = styled.div`
    font-size: 12px;
    font-weight: 900;
    padding-bottom: 2px;
`;

export const EntryUsername = styled.div`
    font-size: 12px;
    font-weight: 300;
    color: var(--default-sub-txt);
    padding-bottom: var(--space-xsm);
`;

export const EntryTitle = styled.div`
    font-size: 14px;
    b {
        font-weight: 900;
    }
`;

export const EntryExtra = styled.ul`
    font-size: 14px;
    padding: 0 0 0 var(--space);
    margin: 0;
    li {
        padding-bottom: var(--space-xsm);
    }
`;

export const Entry = {
    Date: EntryDate,
    Username: EntryUsername,
    Title: EntryTitle,
    Extra: EntryExtra,
};

export const TimelineEntry = ({entry, status, icon, children}) => {
    return (
        <Timeline.Item key={entry.id} status={status} icon={icon}>
            <Entry.Date>{formatDate(entry.createdAt)}</Entry.Date>
            <Entry.Username>{entry.createdByName}</Entry.Username>
            {children}
        </Timeline.Item>
    );
};

/**
 * Counts the total number of updates in a list of field updates. Includes the fileds of 'sub' updates in the count.
 * @param {(import('./FieldUpdate').FieldUpdate | import('./FieldUpdate').SubFieldUpdateUI)[]} updates An array with field updates.
 * @returns {number} The total number of updates.
 */
const countTotalUpdates = (updates) =>
    _.reduce(
        updates,
        (total, update) => {
            if (update.type === 'sub') return total + update.updates.length;
            return total + 1;
        },
        0,
    );

export const TimelineEntryWithUpdates = ({entry, status, icon, children}) => {
    const MINIMUM_UPDATES_TO_SHOW = 3;

    const {t} = useTranslation();
    const processedUpdates = processFieldUpdates(entry.updates, (key) =>
        t(`warehousing.timeline.${entry.type}.fields.${key}`),
    );
    const totalUpdates = countTotalUpdates(processedUpdates);
    const [showAllUpdates, setShowAllUpdates] = React.useState(totalUpdates <= MINIMUM_UPDATES_TO_SHOW);
    const canHideUpdates = totalUpdates > MINIMUM_UPDATES_TO_SHOW;
    const visibleUpdates = showAllUpdates ? processedUpdates : processedUpdates.slice(0, MINIMUM_UPDATES_TO_SHOW);

    const toggleShowAllUpdates = () => setShowAllUpdates((x) => !x);

    return (
        <TimelineEntry entry={entry} status={status} icon={icon}>
            {children}
            <Entry.Extra>
                <>
                    {visibleUpdates.map((update) => (
                        <TimelineEntryFieldUpdate
                            key={update.name}
                            update={update}
                            maxSubfields={showAllUpdates ? Infinity : 0}
                            t={t}
                        />
                    ))}
                    {canHideUpdates && (
                        <Btn color="primary" link onClick={toggleShowAllUpdates}>
                            {!showAllUpdates && (
                                <>
                                    {t('warehousing.timeline.viewAllChanges')} <Icon icon="add" />
                                </>
                            )}
                            {showAllUpdates && (
                                <>
                                    {t('warehousing.timeline.hideChanges')} <Icon icon="remove" />
                                </>
                            )}
                        </Btn>
                    )}
                </>
            </Entry.Extra>
        </TimelineEntry>
    );
};

export const formatDate = (date) => {
    return moment(date).format('DD-MM-YYYY');
};
